import type { Suggestions, Wish, WishOffer } from "@corklog/shared";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import {
  createWish,
  deleteWish,
  fetchSuggestions,
  fetchWishes,
  saveWishSearch,
  searchWishesNow,
  updateWish,
} from "../api.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { actionButton } from "../components/controls.js";
import { emptyWine, WineForm, type WineFormValues } from "../components/WineForm.js";
import { countryName } from "../countries.js";
import { locale } from "../locale.js";
import { useReload } from "../offline.js";
import { useRefreshSession, useSignedInUser } from "../session.js";
import { wishFormValues } from "../wish.js";

/**
 * The wines the reader would like to have — task 10.64.
 *
 * Decided 2026-10-17 in `project-scope.md`, and the screen is mostly an
 * argument for not building one: the form is the registration's own
 * `WineForm`, so what a wish knows is exactly what a wine knows, and the list
 * is a list.
 *
 * **Private, and the screen does not need to say so on every row.** The server
 * answers with the reader's own wishes and nobody's else, so there is nothing
 * here to filter; the intro says it once.
 *
 * **Buying is the registration with the wish in its hand.** *Köpt* navigates
 * to the ordinary form with the wish as router state, and `WineEditor` marks
 * the wish met once the wine exists — in that order, so a registration that
 * fails leaves the wish open rather than met with nothing to show for it.
 *
 * **A met wish is kept**, struck out under its own heading, for the reason in
 * the decision: the list remembers what was wished for and when it came.
 */
type State =
  { status: "loading" } | { status: "ready"; wishes: Wish[] } | { status: "failed" };

/** What the form is open on: nothing, a new wish, or one being corrected. */
type Editing = null | "new" | Wish;

export function Wishes() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cellarId = "" } = useParams();

  const [state, setState] = useState<State>({ status: "loading" });
  const [editing, setEditing] = useState<Editing>(null);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [removeFailed, setRemoveFailed] = useState(false);
  const user = useSignedInUser();
  const refreshSession = useRefreshSession();
  const [searchFailed, setSearchFailed] = useState(false);
  /** *Sök nu* in flight, and what the last press said. */
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<
    null | { searched: number; failed: number; more: boolean } | "failed"
  >(null);

  // A press takes most of a minute — four wishes searched together — so the
  // button says so while it waits and the list is read again when it is done.
  const searchNow = () => {
    setSearching(true);
    setSearchResult(null);
    void searchWishesNow()
      .then((result) => {
        setSearchResult(result);
        load();
      })
      .catch(() => setSearchResult("failed"))
      .finally(() => setSearching(false));
  };

  // The weekly search is the account's own choice, saved when ticked, and the
  // session is asked again so the tick shows what the server holds. Drawn only
  // where the server says it is available — a guest is shown nothing.
  const changeSearch = (enabled: boolean) => {
    setSearchFailed(false);
    void saveWishSearch(enabled)
      .then(() => refreshSession())
      .catch(() => setSearchFailed(true));
  };

  const load = useCallback(() => {
    void fetchWishes(cellarId)
      .then((wishes) => setState({ status: "ready", wishes }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);
  useReload(load);

  // The pickers' words, so a wish is spelled the way the cellar already spells
  // a producer. Suggesting without constraining, as on the registration.
  useEffect(() => {
    void fetchSuggestions(cellarId)
      .then(setSuggestions)
      .catch(() => setSuggestions(null));
  }, [cellarId]);

  const save = async (values: WineFormValues) => {
    if (editing !== null && editing !== "new") {
      await updateWish(cellarId, editing.id, values);
    } else {
      await createWish(cellarId, values);
    }
    setEditing(null);
    load();
  };

  const remove = async (wish: Wish) => {
    setRemoveFailed(false);
    try {
      await deleteWish(cellarId, wish.id);
      load();
    } catch {
      setRemoveFailed(true);
    }
  };

  const buy = (wish: Wish) => {
    void navigate(`/kallare/${cellarId}/viner/nytt`, { state: { wish } });
  };

  const open =
    state.status === "ready" ? state.wishes.filter((w) => w.metAt === null) : [];
  const met =
    state.status === "ready" ? state.wishes.filter((w) => w.metAt !== null) : [];

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="wishes" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("wishes.intro")}</p>

      {user?.wishSearch.available === true && (
        <div className="border-line mt-4 max-w-prose border-y py-3">
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={user.wishSearch.enabled}
              onChange={(event) => changeSearch(event.target.checked)}
              className="accent-accent mt-1"
            />
            <span>
              {t("wishes.searchLabel")}
              <span className="text-muted mt-0.5 block text-xs">
                {t("wishes.searchHelp")}
              </span>
            </span>
          </label>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <button
              type="button"
              onClick={searchNow}
              disabled={searching}
              className={actionButton}
            >
              {searching ? t("wishes.searchingNow") : t("wishes.searchNow")}
            </button>
            {searchResult === "failed" && (
              <span role="alert" className="text-past">
                {t("wishes.searchNowFailed")}
              </span>
            )}
            {searchResult !== null && searchResult !== "failed" && (
              <span role="status" className="text-muted">
                {t("wishes.searchedNow", { count: searchResult.searched })}
                {searchResult.failed > 0 &&
                  ` ${t("wishes.searchedNowFailed", { count: searchResult.failed })}`}
                {searchResult.more && ` ${t("wishes.searchedNowMore")}`}
              </span>
            )}
          </div>
          {searchFailed && (
            <p role="alert" className="text-past mt-2 text-sm">
              {t("wishes.searchFailed")}
            </p>
          )}
        </div>
      )}

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("wishes.loadFailed")}
        </p>
      )}
      {removeFailed && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("wishes.removeFailed")}
        </p>
      )}

      {editing !== null && (
        <div className="mt-6">
          <h2 className="font-display text-xl">
            {editing === "new" ? t("wishes.adding") : t("wishes.correcting")}
          </h2>
          <div className="mt-4">
            <WineForm
              // Keyed on the wish so that opening a second one does not keep
              // the first one's typing: the form reads `initial` once.
              key={editing === "new" ? "new" : editing.id}
              initial={editing === "new" ? emptyWine : wishFormValues(editing)}
              suggestions={suggestions}
              submitLabel={editing === "new" ? t("wishes.save") : t("wishes.saveEdit")}
              busyLabel={t("wines.saving")}
              onCancel={() => setEditing(null)}
              onSubmit={save}
              asWish
              cellarId={cellarId}
            />
          </div>
        </div>
      )}

      {state.status === "ready" && editing === null && (
        <>
          <button
            type="button"
            onClick={() => setEditing("new")}
            className={`${actionButton} mt-6`}
          >
            {t("wishes.add")}
          </button>

          {state.wishes.length === 0 && (
            <p className="text-muted mt-8 max-w-prose text-sm">{t("wishes.empty")}</p>
          )}

          {open.length > 0 && (
            <>
              <h2 className="font-display text-ink mt-8 text-base">{t("wishes.open")}</h2>
              <ul className="border-line mt-3 border-t">
                {open.map((wish) => (
                  <WishRow
                    key={wish.id}
                    wish={wish}
                    onBuy={() => buy(wish)}
                    onChange={() => setEditing(wish)}
                    onRemove={() => void remove(wish)}
                  />
                ))}
              </ul>
            </>
          )}

          {met.length > 0 && (
            <>
              <h2 className="font-display text-ink mt-8 text-base">{t("wishes.met")}</h2>
              <ul className="border-line mt-3 border-t">
                {met.map((wish) => (
                  <WishRow key={wish.id} wish={wish} onRemove={() => void remove(wish)} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}

/**
 * One wish. The actions a met wish does not have are simply not passed: it
 * cannot be bought again or corrected, and a button that was drawn and refused
 * would be a promise the route breaks with a 409.
 */
function WishRow({
  wish,
  onBuy,
  onChange,
  onRemove,
}: {
  wish: Wish;
  onBuy?: () => void;
  onChange?: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const year = wish.isNonVintage
    ? t("wines.nvShort")
    : wish.vintage === null
      ? ""
      : String(wish.vintage);
  const met = wish.metAt !== null;
  const searchedOn =
    wish.searchedAt === null || wish.searchedAt === undefined
      ? null
      : new Date(wish.searchedAt).toLocaleDateString(locale(), {
          year: "numeric",
          month: "long",
          day: "numeric",
        });

  return (
    <li className="border-line border-b py-4">
      <div className="flex items-baseline justify-between gap-4">
        <h3
          className={`font-display truncate text-lg leading-tight ${met ? "text-muted line-through" : ""}`}
        >
          {wish.name}
        </h3>
        {met && wish.metAt !== null && (
          <span className="text-muted shrink-0 text-sm">
            {t("wishes.boughtOn", {
              day: new Date(wish.metAt).toLocaleDateString(locale(), {
                year: "numeric",
                month: "long",
                day: "numeric",
              }),
            })}
          </span>
        )}
      </div>
      <p className="text-muted mt-0.5 truncate text-sm">
        {wish.producer}
        {year !== "" && <>, {year}</>} · {t(`wineType.${wish.type}`)} ·{" "}
        {countryName(wish.countryCode)}
      </p>

      {!met && wish.offers.length > 0 && (
        <div className="mt-2 text-sm">
          <p className="text-muted text-xs">
            {t("wishes.offers")}
            {searchedOn !== null && <> · {t("wishes.searched", { day: searchedOn })}</>}
          </p>
          <ul className="mt-1 space-y-0.5">
            {wish.offers.map((offer) => (
              <OfferRow key={offer.url} offer={offer} />
            ))}
          </ul>
        </div>
      )}
      {!met && wish.offers.length === 0 && searchedOn !== null && (
        <p className="text-muted mt-2 text-xs">
          {t("wishes.noOffers", { day: searchedOn })}
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {onBuy !== undefined && (
          <button
            type="button"
            onClick={onBuy}
            className="text-accent underline-offset-4 hover:underline"
          >
            {t("wishes.bought")}
          </button>
        )}
        {onChange !== undefined && (
          <button
            type="button"
            onClick={onChange}
            className="text-accent underline-offset-4 hover:underline"
          >
            {t("wishes.change")}
          </button>
        )}
        <button
          type="button"
          onClick={onRemove}
          className="text-muted underline-offset-4 hover:underline"
        >
          {t("wishes.remove")}
        </button>
      </div>
    </li>
  );
}

/**
 * One page that sells the wine. The price is written as the shop states it —
 * the amount and its own currency, never converted — and the link opens the
 * page itself, since what the model read is there to be checked.
 */
function OfferRow({ offer }: { offer: WishOffer }) {
  const { t } = useTranslation();
  const price =
    offer.price === null || offer.currency === null
      ? t("wishes.noPrice")
      : `${offer.price.toLocaleString(locale(), { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${offer.currency}`;
  return (
    <li className="flex items-baseline justify-between gap-4">
      <a
        href={offer.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-accent truncate underline-offset-4 hover:underline"
      >
        {offer.shop}
      </a>
      <span className="text-muted shrink-0 tabular-nums">{price}</span>
    </li>
  );
}
